


import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import { DefaultValues, UseFormReturn } from 'react-hook-form';
import { TFunction, useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import { useHistory } from 'react-router-dom';
import {
  ApiButton,
  Box,
  Button,
  Grid,
  Icon,
  Link,
  summonDownload,
  TextFieldForm,
  Typography,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { ResourceResponse } from '../../../api-refactored/onlineTutorials/useGetResource';
import { useGetDocumentDownloadAsAttachment } from '../../../api-refactored/onlineTutorials/useGetDocumentDownload';
import { KeyValueResponse } from '../../../api-refactored/typings';
import ControlledAutocomplete from '../../../components/form-items/ControlledAutocomplete';
import { ResourceFormData } from '../../../models';
import { formatBytes } from '../../../utils/FileUtils';
import { useResourceForm } from '../logic/useResourceForm';
import UploadDocumentTutorialsForm from './UploadDocumentTutorialsForm';

const useStyles = makeStyles()((theme) =>
({
  backButton: {
    marginRight: theme.spacing(2),
  },
  buttonContainer: {
    marginTop: theme.spacing(7.5),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(8),
  },
  input: {
    marginTop: theme.spacing(4),
  },
  inputSelect: {
    marginTop: theme.spacing(7),
  },
  uploadDocumentTitle: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(4),
  },
  existingFile: {
    color: theme.color.text.textHeader,
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(2.5),
  },
  filenameRow: {
    marginTop: theme.spacing(2),
    alignItems: 'center',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    justifySelf: 'start',
    marginRight: theme.spacing(2),
  },
  filenameResolved: {
    textOverflow: 'ellipsis',
    fontWeight: 'bold',
    color: theme.color.link.linkDefault01,
    flexGrow: 2,
  },
  documentIcon: {
    color: theme.color.text.textTertiary,
  },
  fileSize: {
    width: theme.spacing(60),
    whiteSpace: 'nowrap',
    color: theme.color.text.textSecondary,
  },
  errString: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(-2),
    marginBottom: theme.spacing(2),
  },
  fileBadgeContainer: {
    height: theme.spacing(4),
    flexWrap: 'nowrap'
  },
  removeCell: {
    width: theme.spacing(12),
  },
}));

interface ButtonContainerProps {
  classes: Record<string,string>;
  t: TFunction;
  goBackClick: () => void;
  postExamSchoolingDetailStatus: MutationStatus;
  isEditMode: boolean;
  postButtonDisabled?: boolean;
}

const ButtonContainer = ({
  classes,
  t,
  goBackClick,
  postExamSchoolingDetailStatus,
  isEditMode,
  postButtonDisabled = false,
}: ButtonContainerProps) => (
  <Box className={classes.buttonContainer}>
    <Button
      id="button-back"
      className={classes.backButton}
      variant="outlined"
      onClick={goBackClick}
    >
      {t('usefulInformation->>ResourceForm.BackToResourceList')}
    </Button>
    <ApiButton
      id="button-confirm"
      className={classes.submitButton}
      status={postExamSchoolingDetailStatus}
      disabled={postButtonDisabled}
      type="submit"
    >
      {!isEditMode
        ? t('usefulInformation->>Listing.AddResource')
        : t('usefulInformation->>Listing.UpdateResource')}
    </ApiButton>
  </Box>
);

interface ExistingFileViewProps {
  t: TFunction;
  classes: Record<string,string>;
  resourceData: ResourceResponse | undefined;
}

const ExistingFileView = ({ t, classes, resourceData }: ExistingFileViewProps) => {
  const { refetch: onClickDownloadFile } = useGetDocumentDownloadAsAttachment({
    uuid: resourceData?.fileKey || '',
    enabled: false,
    onSuccess: (res) => {
      if (res.url) {
        summonDownload(res.url);
      }
    },
  });

  return (<>
    <Typography className={classes.existingFile} variant="h5">
      {t('usefulInformation->>ResourceForm.ExistingFile')}
    </Typography>
    <Grid
      container
      md={8}
      xs={12}
      direction="row"
      alignItems="center"
      justify="flex-start"
      wrap="nowrap"
      className={classes.fileBadgeContainer}
    >
      <Grid className={classes.iconContainer}>
        <Icon size="S" name={IconName.document} className={classes.documentIcon} />
      </Grid>
      <div onClick={() => onClickDownloadFile()}>{/* NOSONAR */}
        <Link noWrap className={classes.filenameResolved}>
          {resourceData?.fileName}
        </Link>
      </div>
      <Grid container justify="flex-end" className={classes.fileSize}>
        {formatBytes(resourceData?.fileSizeInBytes || 0)}
      </Grid>
    </Grid>
  </>);
};

const createDefaultFormData = (
  examLevelData: KeyValueResponse[],
  resourceData: ResourceResponse | undefined
): DefaultValues<ResourceFormData> => {
  const examLevelDefault = examLevelData.find((v) => v.description === resourceData?.examLevel);
  return {
    description: resourceData?.description,
    examLevel: examLevelDefault,
    title: resourceData?.title,
  };
};

interface UseRemaingTextProps {
  formHook: UseFormReturn<ResourceFormData>;
}

const MAX_TITLE_CHARACTERS = 50;
const MAX_DESCRIPTION_CHARACTERS = 200;

const useRemainingText = ({ formHook }: UseRemaingTextProps) => {
  const { t } = useTranslation();

  const titleRemainingDefault = t('usefulInformation->>ResourceForm.MaximumCharacters', {
    MaximumNumber: MAX_TITLE_CHARACTERS,
  });
  const descriptionRemainingDefault = t('usefulInformation->>ResourceForm.MaximumCharacters', {
    MaximumNumber: MAX_DESCRIPTION_CHARACTERS,
  });
  const { trigger, watch } = formHook;
  const { description: descriptionWatch, title: titleWatch } = watch();

  const [titleRemaining, setTitleRemaining] = useState<string>(titleRemainingDefault);
  const [descriptionRemaining, setDescriptionRemaining] = useState<string>(
    descriptionRemainingDefault
  );

  useEffect(() => {
    if (!titleWatch) {
      setTitleRemaining(titleRemainingDefault);
      return;
    }

    if (titleWatch && titleWatch.length <= MAX_TITLE_CHARACTERS) {
      setTitleRemaining(
        t('usefulInformation->>ResourceForm.RemainingCharacters', {
          RemainingNumber: MAX_TITLE_CHARACTERS - titleWatch.length,
        })
      );
    } else {
      setTitleRemaining(
        t('usefulInformation->>ResourceForm.ExceededErrorMessage', {
          ExceedNumber: titleWatch.length - MAX_TITLE_CHARACTERS,
        })
      );
    }
  }, [t, titleRemainingDefault, titleWatch, trigger]);

  useEffect(() => {
    if (!descriptionWatch) {
      setDescriptionRemaining(descriptionRemainingDefault);
      return;
    }
    if (descriptionWatch.length <= MAX_DESCRIPTION_CHARACTERS) {
      setDescriptionRemaining(
        t('usefulInformation->>ResourceForm.RemainingCharacters', {
          RemainingNumber: MAX_DESCRIPTION_CHARACTERS - descriptionWatch.length,
        })
      );
    } else {
      setDescriptionRemaining(
        t('usefulInformation->>ResourceForm.ExceededErrorMessage', {
          ExceedNumber: descriptionWatch.length - MAX_DESCRIPTION_CHARACTERS,
        })
      );
    }
  }, [descriptionRemainingDefault, descriptionWatch, t, trigger]);

  return {
    titleRemaining,
    descriptionRemaining,
    isExceedTitle: !!titleWatch && titleWatch.length > MAX_TITLE_CHARACTERS,
    isExceedDescription: !!descriptionWatch && descriptionWatch.length > MAX_DESCRIPTION_CHARACTERS,
  };
};

const checkShowTextHelper = (isHasValue: boolean, isError: boolean, isExceed: boolean) => {
  switch (true) {
    case isHasValue:
      return true;
    case isError && !isExceed:
      return false;
    default:
      return true;
  }
};

interface ResourceFormDataViewProps {
  isEditMode?: boolean;
  examLevelData: KeyValueResponse[];
  resourceTypeData?: KeyValueResponse[];
  resourceData?: ResourceResponse;
}

const ResourceFormDataView = ({
  isEditMode = false,
  examLevelData,
  resourceData,
  resourceTypeData,
}: ResourceFormDataViewProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const defaultFormValues = isEditMode
    ? createDefaultFormData(examLevelData, resourceData)
    : undefined;

  const {
    formHook,
    draftFiles,
    fileErrorMessage,
    apiButtonStatus,
    files,
    setFileErrorMessage,
    setFiles,
    onHandleSubmit,
    setDraftFiles,
  } = useResourceForm({
    defaultFormValues,
    isEditMode,
    resourceData,
    resourceTypeData,
  });
  const { control, formState: { errors }, watch } = formHook;
  const { description: descriptionWatch, title: titleWatch } = watch();

  const {
    descriptionRemaining,
    titleRemaining,
    isExceedDescription,
    isExceedTitle,
  } = useRemainingText({ formHook });

  return (
    <form onSubmit={onHandleSubmit}>
      <ControlledAutocomplete
        className={classes.inputSelect}
        id="input-select-exam-level"
        name="examLevel"
        control={control}
        label={t('usefulInformation->>Listing.ExamLevel')}
        options={examLevelData || []}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
        error={!!errors?.examLevel}
        helperText={errors?.examLevel?.message?.toString()}
        isRequired
        disableClearable
        disabled={isEditMode}
      />
      <TextFieldForm
        fullWidth
        variant="outlined"
        id="title"
        name="title"
        error={!!errors?.title || isExceedTitle}
        helperText={
          checkShowTextHelper(!!titleWatch, !!errors?.title, isExceedTitle)
            ? titleRemaining
            : errors?.title?.message
        }
        title={t('usefulInformation->>Listing.Title')}
        label={t('usefulInformation->>Listing.Title')}
        form={formHook}
        isRequired
        className={classes.input}
      />
      <TextFieldForm
        fullWidth
        variant="outlined"
        id="description"
        name="description"
        error={!!errors?.description || isExceedDescription}
        helperText={
          checkShowTextHelper(!!descriptionWatch, !!errors?.description, isExceedDescription)
            ? descriptionRemaining
            : errors?.description?.message
        }
        title={t('usefulInformation->>ResourceForm.Description')}
        label={t('usefulInformation->>ResourceForm.Description')}
        form={formHook}
        isRequired
        className={classes.input}
        multiline
        rows={5}
      />
      {isEditMode && <ExistingFileView t={t} classes={classes} resourceData={resourceData} />}

      <Typography className={classes.uploadDocumentTitle} variant="h3">
        {isEditMode
          ? t('usefulInformation->>ResourceForm.ReplaceDocument')
          : t('usefulInformation->>ResourceForm.UploadDocument')}
      </Typography>
      <UploadDocumentTutorialsForm
        files={files}
        setFiles={setFiles}
        draftFiles={draftFiles}
        setDraftFiles={setDraftFiles}
        isLoading={apiButtonStatus === 'loading'}
        setFileErrorMessage={setFileErrorMessage}
      />
      {fileErrorMessage && (
        <Typography variant="body2" className={classes.errString}>
          {fileErrorMessage}
        </Typography>
      )}
      <ButtonContainer
        isEditMode={isEditMode}
        classes={classes}
        goBackClick={history.goBack}
        postExamSchoolingDetailStatus={apiButtonStatus}
        postButtonDisabled={!formHook.formState.isDirty && files.length === 0 && isEditMode}
        t={t}
      />
    </form>
  );
};

export default ResourceFormDataView;
